import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { path } from '@/consts';
import { WorkspaceBreadcrumbsContext } from '@/containers/WorkspaceObject/Context';
import { ChatContextType } from '@/enums/chat';
import { useSelectGroup } from '@/store/selectors';
import { type BrandInsightsPageContext } from '@/types/context';
import { BrandLoadingContext, BrandStateContext } from './Context';
import { useFetchBrandQuery } from './hooks/useFetchBrandQuery';
import { useRegisterChatPageContext } from '@trinity/chat-components/components/Chat/hooks/useRegisterPageContext.js';

type Props = ChildrenProps;

export const WorkspaceBrandContainer = (props: Props) => {
  const params = useBrandParams();
  const navigate = useNavigate();
  const query = useFetchBrandQuery({
    brandId: params.brandId,
  });

  useEffect(() => {
    if (query.isError && query.error?.status === 404) {
      navigate(path.Workspaces.BrandNotFound, { replace: true });
    }
  }, [query.isError, query.error, navigate]);

  const state = {
    capabilities: query.data?.capabilities,
    brand: query.data?.brand,
    features: query.data?.features,
    object: query.data?.object,
    meta: query.data?.meta,
  };

  const brandContext: BrandInsightsPageContext.Brand = useMemo(() => {
    // if (!query.data?.features?.insightsChat) return null;

    return {
      type: ChatContextType.Brand,
      data: {
        brandId: query.data?.brand?.id,
        objectId: query.data?.object?.id,
      },
      metadata: {
        id: query.data?.brand?.id,
        name: query.data?.brand?.name,
      },
    };
  }, [
    query.data?.features?.insightsChat,
    query.data?.object?.id,
    query.data?.brand?.id,
    query.data?.brand?.name,
  ]);

  const register = useRegisterChatPageContext();

  useEffect(() => {
    if (!brandContext) return;

    if (brandContext.data.objectId) {
      register(brandContext);
    }
  }, [register, brandContext]);

  return (
    <WorkspaceBreadcrumbsContext.Provider value={query.data?.breadcrumbs}>
      <BrandLoadingContext.Provider value={query.isLoading || query.isError}>
        <BrandStateContext.Provider value={state}>
          {props.children}
        </BrandStateContext.Provider>
      </BrandLoadingContext.Provider>
    </WorkspaceBreadcrumbsContext.Provider>
  );
};

type RouteParams = {
  brandId: string;
};

const useBrandParams = () => {
  const params = useParams<RouteParams>();
  return {
    brandId: +params.brandId,
  };
};
